import React from 'react';
import { Icon } from '@iconify/react';

/**
 * Every data screen has four states, not two.
 *
 *   loading → skeleton shaped like the content (not a blank page)
 *   error   → red panel saying what failed, with Retry. Never zeros or
 *             "No items found" when the call failed.
 *   empty   → says why it's empty and what to do next
 *   data    → the children
 *
 * Usage:
 *   const { loading, error, retry } = ...;
 *   <DataState loading={loading} error={error} onRetry={retry}
 *              empty={rows.length === 0} emptyTitle="No plans yet"
 *              emptyHint="Create your first plan." emptyAction={<button…/>}>
 *     <Table rows={rows} />
 *   </DataState>
 *
 * `error` may be a string, an Error/axios error, or true.
 */

/** Turn an axios / fetch error into one plain sentence. */
export const describeError = (err, what = 'this data') => {
  if (!err) return null;
  if (typeof err === 'string') return err;
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail || err?.response?.data?.error?.message;
  if (err?.isCrm && (status === 401 || status === 403)) {
    return `The CRM backend refused this request${detail ? `: ${detail}` : '.'}`;
  }
  if (status === 401) return 'Your sign-in has expired. Please sign in again.';
  if (status === 403) return `You don't have permission to see ${what}.`;
  if (status === 404) return `The server doesn't have ${what} (error 404). This screen's API address is wrong or not built yet.`;
  if (status >= 500) return `The server failed while loading ${what} (error ${status}).`;
  if (status) return `Could not load ${what} (error ${status}).`;
  if (err?.code === 'ECONNABORTED') return `Loading ${what} timed out. Check your connection and retry.`;
  if (err?.message === 'Network Error') return `Could not reach the server to load ${what}. Check your connection and retry.`;
  return `Could not load ${what}.`;
};

export const ErrorPanel = ({ error, onRetry, what, compact = false }) => (
  <div
    role="alert"
    data-testid="data-error"
    className={`flex ${compact ? 'items-center' : 'flex-col sm:flex-row sm:items-center'} gap-3 rounded-xl border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/20 ${compact ? 'p-3' : 'p-4'}`}
  >
    <Icon icon="solar:danger-triangle-linear" className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" aria-hidden="true" />
    <p className="text-sm text-red-700 dark:text-red-300 flex-1">{describeError(error, what)}</p>
    {onRetry && (
      <button
        type="button"
        onClick={onRetry}
        className="self-start sm:self-auto min-h-[40px] px-4 text-sm font-medium rounded-lg bg-white dark:bg-gray-800 border border-red-300 dark:border-red-800 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
      >
        Retry
      </button>
    )}
  </div>
);

export const Skeleton = ({ rows = 5, variant = 'rows' }) => {
  if (variant === 'cards') {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" aria-hidden="true">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="h-40 rounded-xl bg-gray-100 dark:bg-gray-700/60 animate-pulse" />
        ))}
      </div>
    );
  }
  return (
    <div className="space-y-2" aria-hidden="true">
      <div className="h-10 rounded-lg bg-gray-100 dark:bg-gray-700/60 animate-pulse" />
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-12 rounded-lg bg-gray-50 dark:bg-gray-700/40 animate-pulse" />
      ))}
    </div>
  );
};

export const EmptyState = ({ icon = 'solar:inbox-linear', title, hint, action }) => (
  <div data-testid="data-empty" className="flex flex-col items-center justify-center text-center py-12 px-4">
    <Icon icon={icon} className="w-12 h-12 mb-3 text-gray-300 dark:text-gray-600" aria-hidden="true" />
    <p className="text-sm font-medium text-primary-black dark:text-white">{title}</p>
    {hint && <p className="text-sm text-accent-gray mt-1 max-w-sm">{hint}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

const DataState = ({
  loading,
  error,
  onRetry,
  what,
  empty = false,
  emptyTitle = 'Nothing here yet',
  emptyHint,
  emptyIcon,
  emptyAction,
  skeleton = 'rows',
  skeletonRows = 5,
  children,
}) => {
  if (loading) {
    return (
      <div role="status" aria-live="polite">
        <span className="sr-only">Loading…</span>
        <Skeleton variant={skeleton} rows={skeletonRows} />
      </div>
    );
  }
  if (error) return <ErrorPanel error={error} onRetry={onRetry} what={what} />;
  if (empty) return <EmptyState icon={emptyIcon} title={emptyTitle} hint={emptyHint} action={emptyAction} />;
  return <>{children}</>;
};

export default DataState;
